import { useState } from "react";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { Toaster, toast } from "sonner";

const contactInfo = [
  { icon: Mail, label: "Email", value: "admin@scriptovative.com", href: "mailto:admin@scriptovative.com" },
  { icon: Phone, label: "Phone", value: "+91 9392047912", href: "tel:+919392047912" },
  { icon: MapPin, label: "Location", value: "Hyderabad, Telangana, India", href: null },
  { icon: Clock, label: "Business Hours", value: "Mon - Sat: 9:00 AM - 7:00 PM IST", href: null },
];

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    const subject = encodeURIComponent(formData.subject || `Inquiry from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company || "N/A"}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:admin@scriptovative.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
    toast.success("Opening your email client to send the message!");
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-right" richColors />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/3 left-1/3 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono" data-testid="contact-badge">
              CONTACT US
            </Badge>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-[1.1] tracking-tight">
              Let's start a{" "}
              <span className="gradient-text">conversation.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl">
              Whether you have a project in mind, need a quote, or just want to 
              learn more about our services — we're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="relative py-24 lg:py-32" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="font-['Outfit'] text-2xl font-bold text-slate-50 mb-6">Get in touch</h2>
                <div className="space-y-6">
                  {contactInfo.map((info, i) => (
                    <div key={i} data-testid={`contact-info-${i}`} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-5 h-5 text-indigo-400" />
                      </div>
                      <div>
                        <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold mb-1">{info.label}</div>
                        {info.href ? (
                          <a href={info.href} className="text-slate-200 text-sm hover:text-indigo-400 transition-colors">
                            {info.value}
                          </a>
                        ) : (
                          <span className="text-slate-200 text-sm">{info.value}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="p-6 rounded-xl bg-slate-900/50 border border-slate-800">
                <h3 className="font-['Outfit'] font-semibold text-slate-200 mb-4 text-sm">Why partner with us?</h3>
                <ul className="space-y-3">
                  {["20+ web applications delivered", "99.9% uptime guarantee", "24-hour response time", "Agile project management"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-400 text-sm">
                      <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div data-testid="contact-success" className="p-12 rounded-2xl bg-slate-900/50 border border-indigo-500/20 text-center">
                  <div className="w-16 h-16 rounded-full bg-indigo-500/10 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-indigo-400" />
                  </div>
                  <h3 className="font-['Outfit'] text-2xl font-bold text-slate-50 mb-3">Message Sent!</h3>
                  <p className="text-slate-400 text-sm max-w-md mx-auto">
                    Thank you for reaching out. Our team will review your message 
                    and get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", company: "", subject: "", message: "" }); }}
                    data-testid="send-another-btn"
                    className="mt-6 text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="p-8 lg:p-10 rounded-2xl bg-slate-900/50 border border-slate-800">
                  <h2 className="font-['Outfit'] text-xl font-bold text-slate-50 mb-8">Send us a message</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Name *</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="contact-name-input"
                        placeholder="Your full name"
                        className="bg-slate-800/50 border-slate-700 text-slate-200 placeholder:text-slate-500 focus:border-indigo-500 focus:ring-indigo-500/20"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Email *</label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-email-input"
                        placeholder="you@company.com"
                        className="bg-slate-800/50 border-slate-700 text-slate-200 placeholder:text-slate-500 focus:border-indigo-500 focus:ring-indigo-500/20"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Company</label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="contact-company-input"
                        placeholder="Company name"
                        className="bg-slate-800/50 border-slate-700 text-slate-200 placeholder:text-slate-500 focus:border-indigo-500 focus:ring-indigo-500/20"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Subject</label>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        data-testid="contact-subject-input"
                        placeholder="What's this about?"
                        className="bg-slate-800/50 border-slate-700 text-slate-200 placeholder:text-slate-500 focus:border-indigo-500 focus:ring-indigo-500/20"
                      />
                    </div>
                  </div>
                  <div className="mb-8">
                    <label className="block text-slate-400 text-sm mb-2">Message *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      data-testid="contact-message-input"
                      placeholder="Tell us about your project, requirements, or questions..."
                      rows={5}
                      className="bg-slate-800/50 border-slate-700 text-slate-200 placeholder:text-slate-500 focus:border-indigo-500 focus:ring-indigo-500/20"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5"
                  >
                    Send Message
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
